import React, { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { AiOutlineSetting, AiOutlineCloudDownload, AiOutlineWarning, AiOutlineCopy, AiOutlineCheck } from 'react-icons/ai';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MonthlyTotalsModule = ({ statementRows = [], rowData }) => {
    const [processedData, setProcessedData] = useState([]);
    const [copied, setCopied] = useState(false);

    // ── localStorage key scoped to this client ──────────────────────────────
    const lsKey = `monthly_totals_${rowData?.client?.id || rowData?.id || 'default'}`;

    // ── Load saved data on mount ─────────────────────────────
    useEffect(() => {
        try {
            const saved = localStorage.getItem(lsKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.processedData?.length > 0) {
                    setProcessedData(parsed.processedData);
                }
            }
        } catch (err) {
            console.warn('MonthlyTotals: failed to load from localStorage', err);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lsKey]);

    // Global Helpers
    function getMKey(cellValue) {
        if (!cellValue) return "";
        const s = String(cellValue).trim();
        const match = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (!match) return "";
        const mm = parseInt(match[2]);
        const yyyy = match[3];
        if (mm < 1 || mm > 12) return "";
        return `${String(mm).padStart(2, "0")}-${yyyy}`;
    }

    function initMonthEntry() {
        return {
            OutInv: { oval: 0, oiamt: 0, ocamt: 0, osamt: 0 },
            OutCN: { oval: 0, oiamt: 0, ocamt: 0, osamt: 0 },
            InReg: { val: 0, iamt: 0, camt: 0, samt: 0 },
            InRCM: { val: 0, iamt: 0, camt: 0, samt: 0 },
            Import: { val: 0, iamt: 0, camt: 0, samt: 0 },
        };
    }

    function safeNum(v) {
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
    }

    function sortMonthKeys(keys) {
        return [...keys].sort((a, b) => {
            const [ma, ya] = a.split("-").map(Number);
            const [mb, yb] = b.split("-").map(Number);
            if (ya !== yb) return ya - yb;
            return ma - mb;
        });
    }

    const formatINR = (val) => {
        const n = parseFloat(val);
        if (isNaN(n) || n === 0) return "—";
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(n);
    };

    const buildMonthlyTotals = () => {
        const map = {};
        for (const row of statementRows) {
            const istype = String(row[1] || "").trim();
            const idt = row[5] || "";
            const outType = String(row[11] || "").trim();
            const outDocType = String(row[12] || "").trim();
            const oidt = row[14] || "";

            // Outward Processing
            const keyOut = getMKey(oidt);
            if (keyOut) {
                if (!map[keyOut]) map[keyOut] = initMonthEntry();
                const isCredit = 
                  outType.toLowerCase().includes("credit") || 
                  outType.toLowerCase().includes("cdn") ||
                  outDocType.toLowerCase().includes("credit") ||
                  outDocType.toLowerCase().includes("cdn");

                const outCat = isCredit ? "OutCN" : "OutInv";
                map[keyOut][outCat].oval += safeNum(row[15]);
                map[keyOut][outCat].oiamt += safeNum(row[16]);
                map[keyOut][outCat].ocamt += safeNum(row[17]);
                map[keyOut][outCat].osamt += safeNum(row[18]);
            }

            // Inward Processing
            const keyIn = getMKey(idt);
            if (keyIn) {
                if (!map[keyIn]) map[keyIn] = initMonthEntry();
                let inCat = "InReg";
                if (istype === "Import of Goods/Supplies from SEZ to DTA") {
                    inCat = "Import";
                } else if (istype.toLowerCase().includes("liable to reverse charge")) {
                    inCat = "InRCM";
                }

                // If the document is a Credit Note (B2B-CDNR), subtract the amounts to net them
                let sign = 1;
                const inDocType = String(row[3] || "").trim().toLowerCase();
                if (inDocType.includes("credit") || inDocType.includes("cdn")) {
                    sign = -1;
                }

                map[keyIn][inCat].val += safeNum(row[7]) * sign;
                map[keyIn][inCat].iamt += safeNum(row[8]) * sign;
                map[keyIn][inCat].camt += safeNum(row[9]) * sign;
                map[keyIn][inCat].samt += safeNum(row[10]) * sign;
            }
        }

        const sortedKeys = sortMonthKeys(Object.keys(map));
        const finalData = sortedKeys.map(key => {
            const m = map[key];
            return [
                key, // 0
                m.OutInv.oval, m.OutInv.oiamt, m.OutInv.ocamt, m.OutInv.osamt, // 1-4
                m.OutCN.oval, m.OutCN.oiamt, m.OutCN.ocamt, m.OutCN.osamt, // 5-8
                m.InReg.val, m.InReg.iamt, m.InReg.camt, m.InReg.samt, // 9-12
                m.InRCM.val, m.InRCM.iamt, m.InRCM.camt, m.InRCM.samt, // 13-16
                m.Import.val, m.Import.iamt, m.Import.camt, m.Import.samt, // 17-20
            ];
        });
        setProcessedData(finalData);

        // ── Save to localStorage ────────────────────────────────
        try {
            localStorage.setItem(lsKey, JSON.stringify({ processedData: finalData }));
        } catch (err) {
            console.warn('MonthlyTotals: failed to save to localStorage', err);
        }
    };

    const downloadExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Monthly Totals');

        // Styles
        const titleStyle = { font: { bold: true, size: 14, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD97706' } }, alignment: { horizontal: 'center' } };
        const groupHeaderStyle = (color) => ({ font: { bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }, alignment: { horizontal: 'center' }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } });
        const subHeaderStyle = { font: { bold: true, size: 9 }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }, border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }, alignment: { horizontal: 'center', wrapText: true } };
        const borderStyle = { border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } } };

        // 1. Title
        sheet.addRow(["MONTHLY AGGREGATED SUMMARY - GST REFUND"]);
        sheet.mergeCells(1, 1, 1, 21);
        sheet.getRow(1).getCell(1).style = titleStyle;
        sheet.getRow(1).height = 25;

        // 2. Group Headers
        const groupRow = sheet.addRow([
            "Month",
            "A. Outward Invoice", "", "", "",
            "B. Outward CN", "", "", "",
            "2A. Inward Reg", "", "", "",
            "2B. Inward RCM", "", "", "",
            "2C. Import/SEZ", "", "", ""
        ]);
        sheet.mergeCells(2, 2, 2, 5); // OutInv
        sheet.mergeCells(2, 6, 2, 9); // OutCN
        sheet.mergeCells(2, 10, 2, 13); // InReg
        sheet.mergeCells(2, 14, 2, 17); // InRCM
        sheet.mergeCells(2, 18, 2, 21); // Import

        groupRow.getCell(1).style = groupHeaderStyle('FF334155');
        groupRow.getCell(2).style = groupHeaderStyle('FF2563EB'); // Blue
        groupRow.getCell(6).style = groupHeaderStyle('FFDC2626'); // Red
        groupRow.getCell(10).style = groupHeaderStyle('FF059669'); // Green
        groupRow.getCell(14).style = groupHeaderStyle('FF7C3AED'); // Purple
        groupRow.getCell(18).style = groupHeaderStyle('FF0891B2'); // Teal

        // 3. Columns Labels
        const subHeaderLabels = [
            "MM-YYYY",
            "Taxable", "IGST", "CGST", "SGST",
            "Taxable", "IGST", "CGST", "SGST",
            "Value", "IGST", "CGST", "SGST",
            "Value", "IGST", "CGST", "SGST",
            "Value", "IGST", "CGST", "SGST"
        ];
        const subHeaderRow = sheet.addRow(subHeaderLabels);
        subHeaderRow.eachCell(cell => { cell.style = subHeaderStyle; });

        // 4. Data Rows
        processedData.forEach(row => {
            const r = sheet.addRow(row);
            r.eachCell(cell => { cell.border = borderStyle.border; });
        });

        // 5. Total Row (Calculate manually for the row since 'totals' might be async state)
        const sums = Array(21).fill(0);
        processedData.forEach(prow => {
             prow.forEach((val, idx) => { if(idx > 0) sums[idx] += (parseFloat(val) || 0); });
        });
        const totalRowArr = ["TOTAL", ...sums.slice(1)];
        const lastRow = sheet.addRow(totalRowArr);
        lastRow.eachCell(cell => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFBEB' } };
            cell.border = borderStyle.border;
        });

        // 6. Formatting
        sheet.columns = [{ width: 12 }, ...Array(20).fill({ width: 14 })];
        sheet.eachRow((row, rowNumber) => {
            if (rowNumber > 3) {
                row.eachCell((cell, colIdx) => {
                    if (colIdx > 1 && typeof cell.value === 'number') {
                        cell.numFmt = '#,##0.00';
                        cell.alignment = { horizontal: 'right' };
                    }
                });
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "Monthly_Totals_Summary.xlsx");
    };

    const copyToTSV = () => {
        if (processedData.length === 0) return;
        const headers = ["Month", "OutInv_Tax", "OutInv_I", "OutInv_C", "OutInv_S", "OutCN_Tax", "OutCN_I", "OutCN_C", "OutCN_S", "InReg_Tax", "InReg_I", "InReg_C", "InReg_S", "InRCM_Tax", "InRCM_I", "InRCM_C", "InRCM_S", "Import_Tax", "Import_I", "Import_C", "Import_S"];
        const tsv = [headers, ...processedData].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(tsv).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const totals = useMemo(() => {
        if (processedData.length === 0) return Array(21).fill(0);
        const sums = Array(21).fill(0);
        processedData.forEach(row => {
            row.forEach((val, i) => {
                if (i > 0) sums[i] += val;
            });
        });
        return sums;
    }, [processedData]);

    const stats = useMemo(() => {
        if (processedData.length === 0) return {};
        return {
            months: processedData.length,
            outInv: processedData.reduce((a, b) => a + b[1], 0),
            outCN: processedData.reduce((a, b) => a + b[5], 0),
            inReg: processedData.reduce((a, b) => a + b[9], 0),
            inRCM: processedData.reduce((a, b) => a + b[13], 0),
            import: processedData.reduce((a, b) => a + b[17], 0)
        };
    }, [processedData]);

    return (
        <div className="monthly-totals-container">
            <style>{`
                .monthly-totals-container {
                    background: #ffffff;
                    color: #1e293b;
                    padding: 24px;
                    border-radius: 12px;
                    font-family: 'DM Mono', monospace;
                }
                .syne { font-family: 'Syne', sans-serif; }
                .stats-bar {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    padding: 16px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 24px;
                    font-size: 13px;
                }
                .stat-box { color: #1e293b; }
                .stat-box span { color: #d97706; font-weight: bold; margin-left: 4px; }
                .btn-build {
                    background: #fffbeb;
                    border: 1px solid #d97706;
                    color: #d97706;
                    padding: 10px 20px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    transition: all 0.3s;
                    margin-bottom: 20px;
                }
                .btn-build:hover { background: #d97706; color: #ffffff; }
                .warning-box {
                    background: rgba(245, 158, 11, 0.08);
                    border: 1px solid #d97706;
                    color: #92400e;
                    padding: 16px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .table-container {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    overflow: hidden;
                    position: relative;
                }
                .table-wrap {
                    max-height: 60vh;
                    overflow: auto;
                }
                table {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0;
                    font-size: 11px;
                    min-width: 1800px;
                }
                th {
                    background: #f8fafc;
                    border-bottom: 2px solid #e2e8f0;
                    border-right: 1px solid #e2e8f0;
                    padding: 12px 8px;
                    text-align: right;
                    font-weight: bold;
                    color: #475569;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                .group-header th {
                    border-bottom: 1px solid #e2e8f0;
                    text-align: center;
                    font-size: 13px;
                }
                thead tr:nth-child(2) th {
                    top: 41px; /* Adjust based on first row height */
                    z-index: 10;
                }
                td {
                    padding: 10px 8px;
                    text-align: right;
                    border-bottom: 1px solid #f1f5f9;
                    border-right: 1px solid #f1f5f9;
                    color: #1e293b;
                }
                .month-col {
                    position: sticky;
                    left: 0;
                    background: #f8fafc !important;
                    z-index: 20;
                    text-align: center;
                    border-right: 2px solid #e2e8f0;
                    min-width: 100px;
                }
                thead .month-col {
                    z-index: 30;
                }
                .footer-total td {
                    background: #fffbeb !important;
                    font-weight: bold;
                    color: #d97706;
                    border-top: 2px solid #d97706;
                }
                .staggered-row:hover td {
                    background: #f1f5f9 !important;
                }
                .muted { color: #cbd5e1; }
                .actions-bar {
                    margin-top: 20px;
                    display: flex;
                    gap: 16px;
                }
                @keyframes spinOnce {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spin-once { animation: spinOnce 0.6s ease-in-out; }
            `}</style>

            <h3 className="syne" style={{ margin: '0 0 20px 0', color: '#d97706' }}>Monthly Totals Builder</h3>

            {statementRows.length === 0 ? (
                <div className="warning-box">
                    <AiOutlineWarning size={24} />
                    <span>No data in Statement 01A — please import GSTR-1 and GSTR-2B first</span>
                </div>
            ) : (
                <>
                    <div className="stats-bar">
                        <div className="stat-box">Total Months: <span>{stats.months || 0}</span></div>
                        <div className="stat-box">Outward Invoice: <span>₹{formatINR(stats.outInv)}</span></div>
                        <div className="stat-box">Outward CN: <span>₹{formatINR(stats.outCN)}</span></div>
                        <div className="stat-box">Inward Reg: <span>₹{formatINR(stats.inReg)}</span></div>
                        <div className="stat-box">Inward RCM: <span>₹{formatINR(stats.inRCM)}</span></div>
                        <div className="stat-box">Import: <span>₹{formatINR(stats.import)}</span></div>
                    </div>

                    <button className="btn-build" onClick={buildMonthlyTotals}>
                        <AiOutlineSetting className={processedData.length > 0 ? '' : 'spin-once'} />
                        {processedData.length > 0 ? 'Rebuild Totals' : '⚙ Build Monthly Totals'}
                    </button>

                    {processedData.length > 0 && (
                        <div className="table-container">
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr className="group-header">
                                            <th className="month-col">Month</th>
                                            <th colSpan="4" style={{ color: '#60a5fa' }}>A. Outward Invoice</th>
                                            <th colSpan="4" style={{ color: '#fb7185' }}>B. Outward CN</th>
                                            <th colSpan="4" style={{ color: '#2dd4bf' }}>2A. Inward Reg</th>
                                            <th colSpan="4" style={{ color: '#a78bfa' }}>2B. Inward RCM</th>
                                            <th colSpan="4" style={{ color: '#34d399' }}>2C. Import/SEZ</th>
                                        </tr>
                                        <tr>
                                            <th className="month-col">MM-YYYY</th>
                                            <th>Output Taxable value</th><th>Output IGST</th><th>Output CGST</th><th>Output SGST</th>
                                            <th>Output Taxable value</th><th>Output IGST</th><th>Output CGST</th><th>Output SGST</th>
                                            <th>Input Taxable value</th><th>Input IGST</th><th>Input CGST</th><th>Input SGST</th>
                                            <th>Input Taxable value</th><th>Input IGST</th><th>Input CGST</th><th>Input SGST</th>
                                            <th>Input Taxable value</th><th>Input IGST</th><th>Input CGST</th><th>Input SGST</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {processedData.map((row, i) => (
                                            <tr key={row[0]} className="staggered-row" style={{ animationDelay: `${i * 0.05}s` }}>
                                                <td className="month-col">{row[0]}</td>
                                                {row.slice(1).map((val, idx) => (
                                                    <td key={idx} className={val === 0 ? 'muted' : ''}>
                                                        {formatINR(val)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        <tr className="footer-total">
                                            <td className="month-col">TOTAL</td>
                                            {totals.slice(1).map((val, idx) => (
                                                <td key={idx}>{formatINR(val)}</td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {processedData.length > 0 && (
                        <div className="actions-bar">
                            <button className="btn-build" onClick={downloadExcel} style={{ marginBottom: 0 }}>
                                <AiOutlineCloudDownload /> Download Monthly_Totals.xlsx
                            </button>
                            <button className="btn-build" onClick={copyToTSV} style={{ marginBottom: 0, border: '1px solid #e2e8f0', color: '#64748b' }}>
                                {copied ? <AiOutlineCheck color="#34d399" /> : <AiOutlineCopy />}
                                {copied ? 'Copied ✓' : 'Copy TSV'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MonthlyTotalsModule;
